import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, Clock, WifiOff, Info, ChevronRight, ShieldAlert, Search, TruckIcon, Phone, Navigation, PackageIcon } from 'lucide-react';
import { Alert, Severity, mockDrivers, mockDeliveries } from '../data/mockData';
interface AlertFeedProps {
  alerts: Alert[];
  selectedAlertId: string | null;
  onSelectAlert: (alertId: string, driverId: string) => void;
  requestedSeverityFilter?: {
    severity: 'all' | 'critical' | 'warning' | 'low';
    ts: number;
  } | null;
}
export function AlertFeed({
  alerts,
  selectedAlertId,
  onSelectAlert,
  requestedSeverityFilter
}: AlertFeedProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'hos' | 'routing' | 'signal'>('all');
  const [severityFilter, setSeverityFilter] = useState<'all' | 'critical' | 'warning' | 'low'>('all');
  const [filterMode, setFilterMode] = useState<'none' | 'type' | 'severity'>('severity');
  // Compute filter counts
  const typeCounts = {
    all: alerts.length,
    hos: alerts.filter(a => a.type === 'hos').length,
    routing: alerts.filter(a => a.type === 'bridge' || a.type === 'traffic').length,
    signal: alerts.filter(a => a.type === 'offline').length
  };
  const severityCounts = {
    all: alerts.length,
    critical: alerts.filter(a => a.severity === 'critical').length,
    warning: alerts.filter(a => a.severity === 'warning').length,
    low: alerts.filter(a => a.severity === 'info' || a.severity === 'offline').length
  };
  useEffect(() => {
    if (requestedSeverityFilter) {
      setSeverityFilter(requestedSeverityFilter.severity);
    }
  }, [requestedSeverityFilter]);
  const severityOrder: Record<Severity, number> = {
    critical: 0,
    warning: 1,
    offline: 2,
    info: 3,
    normal: 4
  };
  const sortedAlerts = [...alerts].sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity]);
  const isVisible = (a: Alert) => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (!a.title.toLowerCase().includes(q) && !a.description.toLowerCase().includes(q)) return false;
    }
    if (typeFilter === 'hos' && a.type !== 'hos') return false;
    if (typeFilter === 'routing' && a.type !== 'bridge' && a.type !== 'traffic') return false;
    if (typeFilter === 'signal' && a.type !== 'offline') return false;
    if (severityFilter === 'critical' && a.severity !== 'critical') return false;
    if (severityFilter === 'warning' && a.severity !== 'warning') return false;
    if (severityFilter === 'low' && a.severity !== 'info' && a.severity !== 'offline') return false;
    return true;
  };
  const visibleCount = sortedAlerts.filter(isVisible).length;
  return <>
      <div className="p-3 border-b border-slate-800 space-y-3 shrink-0" data-id="element-15">
        {/* Filter Mode Tabs */}
        <div className="flex gap-1 bg-slate-900 p-1 rounded-lg border border-slate-800" data-id="element-16">
          <button onClick={() => {
          if (filterMode === 'severity') {
            setFilterMode('none');
          } else {
            setFilterMode('severity');
            setTypeFilter('all');
          }
        }} className={`flex-1 text-[10px] py-1.5 rounded-md transition-colors font-medium ${filterMode === 'severity' ? 'bg-slate-800 text-slate-100 shadow-sm' : 'text-slate-500 hover:text-slate-300'}`} data-id="element-17">
            Severity
          </button>
          <button onClick={() => {
          if (filterMode === 'type') {
            setFilterMode('none');
          } else {
            setFilterMode('type');
            setSeverityFilter('all');
          }
        }} className={`flex-1 text-[10px] py-1.5 rounded-md transition-colors font-medium ${filterMode === 'type' ? 'bg-slate-800 text-slate-100 shadow-sm' : 'text-slate-500 hover:text-slate-300'}`} data-id="element-18">
            Type
          </button>
        </div>

        {/* Severity Filter Options */}
        {filterMode === 'severity' && <div className="flex gap-1 bg-slate-900 p-1 rounded-lg border border-slate-800" data-id="element-19">
            <FilterTab active={severityFilter === 'all'} onClick={() => setSeverityFilter('all')} label="All" count={severityCounts.all} data-id="element-20" />
            <FilterTab active={severityFilter === 'critical'} onClick={() => setSeverityFilter('critical')} label="Critical" count={severityCounts.critical} data-id="element-21" />
            <FilterTab active={severityFilter === 'warning'} onClick={() => setSeverityFilter('warning')} label="Warn" count={severityCounts.warning} data-id="element-22" />
            <FilterTab active={severityFilter === 'low'} onClick={() => setSeverityFilter('low')} label="Low" count={severityCounts.low} data-id="element-23" />
          </div>}

        {/* Type Filter Options */}
        {filterMode === 'type' && <div className="flex gap-1 bg-slate-900 p-1 rounded-lg border border-slate-800" data-id="element-24">
            <FilterTab active={typeFilter === 'all'} onClick={() => setTypeFilter('all')} label="All" count={typeCounts.all} data-id="element-25" />
            <FilterTab active={typeFilter === 'hos'} onClick={() => setTypeFilter('hos')} label="HOS" count={typeCounts.hos} data-id="element-26" />
            <FilterTab active={typeFilter === 'routing'} onClick={() => setTypeFilter('routing')} label="Routing" count={typeCounts.routing} data-id="element-27" />
            <FilterTab active={typeFilter === 'signal'} onClick={() => setTypeFilter('signal')} label="Signal" count={typeCounts.signal} data-id="element-28" />
          </div>}

        {/* Search */}
        <div className="relative" data-id="element-29">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" data-id="element-30" />
          <input type="text" placeholder="Search alerts..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full bg-slate-900 border border-slate-800 rounded-lg pl-9 pr-3 py-2 text-xs text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-slate-600 transition-colors" data-id="element-31" />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-3 scrollbar-hide" data-id="element-32">
        {sortedAlerts.map(alert => <AlertCard key={alert.id} alert={alert} isSelected={selectedAlertId === alert.id} onClick={() => onSelectAlert(alert.id, alert.driverId)} hidden={!isVisible(alert)} data-id="element-33" />)}

        {visibleCount === 0 && <div className="text-center p-8 text-slate-500 text-sm" data-id="element-34">
            <ShieldAlert className="w-8 h-8 mx-auto mb-3 opacity-20" data-id="element-35" />
            No active alerts match your filters.
          </div>}
      </div>
    </>;
}
function FilterTab({
  active,
  onClick,
  label,
  count
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count?: number;
}) {
  return <button onClick={onClick} className={`flex-1 text-[10px] py-1.5 rounded-md transition-colors font-medium flex items-center justify-center gap-1.5
        ${active ? 'bg-slate-800 text-slate-100 shadow-sm' : 'text-slate-500 hover:text-slate-300'}`} data-id="element-36">
      {label}
      {count !== undefined && <span className={`min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold flex items-center justify-center ${active ? 'bg-slate-700 text-slate-300' : 'bg-slate-800 text-slate-500'}`} data-id="element-37">
          {count}
        </span>}
    </button>;
}
function AlertCard({
  alert,
  isSelected,
  onClick,
  hidden
}: {
  alert: Alert;
  isSelected: boolean;
  onClick: () => void;
  hidden: boolean;
}) {
  const config = {
    critical: {
      icon: AlertTriangle,
      color: 'text-brand-red',
      bg: 'bg-brand-red/10',
      border: 'border-brand-red/30',
      pulse: true
    },
    warning: {
      icon: AlertTriangle,
      color: 'text-brand-amber',
      bg: 'bg-brand-amber/10',
      border: 'border-brand-amber/30',
      pulse: false
    },
    offline: {
      icon: WifiOff,
      color: 'text-slate-400',
      bg: 'bg-slate-800/50',
      border: 'border-slate-700',
      pulse: false
    },
    info: {
      icon: Info,
      color: 'text-brand-blue',
      bg: 'bg-brand-blue/10',
      border: 'border-brand-blue/30',
      pulse: false
    },
    normal: {
      icon: Info,
      color: 'text-slate-400',
      bg: 'bg-slate-800',
      border: 'border-slate-700',
      pulse: false
    }
  }[alert.severity];
  const Icon = config.icon;
  if (hidden) return null;
  return <div onClick={onClick} className={`relative p-3 mb-3 last:mb-0 rounded-xl border cursor-pointer transition-all
        ${isSelected ? `${config.bg} ${config.border} shadow-[0_0_15px_rgba(0,0,0,0.2)]` : 'bg-slate-900/50 border-slate-800 hover:border-slate-700'}
      `} data-id="element-38">
      {isSelected && <motion.div layoutId="activeAlertIndicator" className={`absolute left-0 top-3 bottom-3 w-1 rounded-r-full ${config.color.replace('text-', 'bg-')}`} data-id="element-39" />}

      <div className="flex items-start gap-3" data-id="element-40">
        <div className={`mt-0.5 relative ${config.color}`} data-id="element-41">
          <Icon className="w-5 h-5" data-id="element-42" />
        </div>

        <div className="flex-1 min-w-0" data-id="element-43">
          <div className="flex items-center justify-between mb-1" data-id="element-44">
            <h3 className={`text-sm font-semibold truncate ${isSelected ? 'text-slate-100' : 'text-slate-300'}`} data-id="element-45">
              {alert.title}
            </h3>
            <span className="text-[10px] font-mono text-slate-500 shrink-0 ml-2" data-id="element-46">
              {alert.timeContext}
            </span>
          </div>

          {alert.affectedDriverIds ? <div className="mb-1.5 space-y-0.5" data-id="element-47">
              <div className="flex items-center gap-1.5 text-brand-amber" data-id="element-48">
                <TruckIcon className="w-3 h-3" data-id="element-49" />
                <span className="text-[10px] font-bold uppercase tracking-wider" data-id="element-50">
                  {alert.affectedDriverIds.length} trucks affected
                </span>
              </div>
              <div className="flex items-center gap-1.5 text-brand-amber" data-id="element-51">
                <PackageIcon className="w-3 h-3" data-id="element-52" />
                <span className="text-[10px] font-bold uppercase tracking-wider" data-id="element-53">
                  {mockDeliveries.filter(d => alert.affectedDriverIds!.includes(d.driverId) && d.status !== 'completed').length}{' '}
                  deliveries affected
                </span>
              </div>
            </div> : (() => {
          const driver = mockDrivers.find(d => d.id === alert.driverId);
          return driver ? <div className="flex items-center gap-1.5 mb-1.5 text-slate-400" data-id="element-54">
                  <TruckIcon className="w-3 h-3" data-id="element-55" />
                  <span className="text-[10px] font-bold uppercase tracking-wider font-mono" data-id="element-56">
                    {driver.truckId}
                  </span>
                </div> : null;
        })()}

          <p className="text-xs text-slate-400 line-clamp-2 mb-2 leading-relaxed" data-id="element-57">
            {alert.description}
          </p>

          {(alert.severity === 'critical' || alert.severity === 'warning') && <>
              {alert.actions && alert.actions.length > 0 ? <div className="flex gap-2 mt-2" data-id="element-58">
                  {alert.actions.map((action, idx) => <button key={idx} className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-[10px] font-semibold transition-colors ${alert.severity === 'critical' ? 'bg-brand-red hover:bg-brand-red/90 text-white' : 'bg-brand-amber hover:bg-brand-amber/90 text-slate-900'}`} onClick={e => e.stopPropagation()} data-id="element-59">
                      {action.icon === 'phone' && <Phone className="w-3 h-3" data-id="element-60" />}
                      {action.icon === 'route' && <Navigation className="w-3 h-3" data-id="element-61" />}
                      {action.label}
                    </button>)}
                </div> : alert.actionHint && alert.actionHint.toLowerCase() !== 'no action needed' ? <button className={`w-full mt-2 py-1.5 rounded-md text-[11px] font-semibold transition-colors ${alert.severity === 'critical' ? 'bg-brand-red hover:bg-brand-red/90 text-white' : 'bg-brand-amber hover:bg-brand-amber/90 text-slate-900'}`} onClick={e => e.stopPropagation()} data-id="element-62">
                  {alert.actionHint}
                </button> : null}
            </>}
        </div>
      </div>
    </div>;
}